import * as React from 'react'
import type { PasswordStrength } from 'types/business'
import PasswordStrengthIndicator from 'components/common/password-strength-indicator'
import Row from 'components/common/row'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import InlineError from 'components/ui/inline-error'
import TextField from 'components/ui/text-field'
import { useTranslation } from '@disco/i18n/use-translation'
import { Trans } from '@disco/i18n/trans'
type Props = {
  password: string
  passwordConfirmation: string
  passwordChangeInProgress?: boolean
  onSubmit: () => unknown
  onPasswordChange: any
  onPasswordConfirmationChange: any
  passwordStrength: PasswordStrength
  errors: Record<string, React.ReactNode | null | undefined>
}

/**
 * Password change form for welcome/account creation flow
 * Includes password confirmation, terms acceptance, and account creation button
 * @param password - New password value
 * @param passwordConfirmation - Password confirmation value
 * @param passwordChangeInProgress - Whether password change is in progress
 * @param onSubmit - Callback when form is submitted
 * @param onPasswordChange - Callback when password input changes
 * @param onPasswordConfirmationChange - Callback when confirmation input changes
 * @param passwordStrength - Business password strength requirements
 * @param errors - Error messages for form validation
 */
const ChangePasswordWelcomeForm = (props: Props): React.ReactElement<'div'> => {
  const { t } = useTranslation('accounts')
  const submitEnabled =
    props.password &&
    props.passwordConfirmation &&
    !props.errors['re-enter-new-password'] &&
    !props.errors['new-password']
  return (
    <div className="ChangePasswordWelcomeForm">
      <Row columnsSize={[12]} marginBottom="small">
        <div>
          <TextField
            label={t('accounts:chooseANewPassword', 'Choose a new password')}
            type="password"
            autoComplete="new-password"
            onChange={props.onPasswordChange}
            value={props.password}
            invalid={!!props.errors['new-password']}
            error={props.errors['new-password']}
          />
          <PasswordStrengthIndicator
            password={props.password}
            passwordStrength={props.passwordStrength}
            error={props.errors['weak-password']}
          />
        </div>
      </Row>
      <Row columnsSize={[12]} marginBottom="small">
        <div>
          <TextField
            label={t('accounts:reEnterPassword', 'Re-enter password')}
            type="password"
            autoComplete="new-password"
            onChange={props.onPasswordConfirmationChange}
            value={props.passwordConfirmation}
            invalid={!!props.errors['re-enter-new-password']}
            error={props.errors['re-enter-new-password']}
          />
        </div>
      </Row>
      <Row columnsSize={[12]} marginBottom="small">
        <div>
          <div className="PostInvites_TermsContainer">
            <Trans
              t={t}
              i18nKey="accounts:byClickingCreateAccountYouAgreeToBeBoundByOurTermsAndPrivacyPolicy"
              defaults="By clicking <bold>Create account</bold> you agree to be bound by our <terms>Terms</terms> and <privacy>Privacy Policy</privacy>"
              components={{
                bold: <b />,
                // Trans fills the children from the translated string, so the
                // rule cannot see the content from here.
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                terms: (
                  <a
                    className="Login_Link"
                    href="https://disco.ac/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                privacy: (
                  <a
                    className="Login_Link"
                    href="https://disco.ac/security"
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
              }}
            />
          </div>
        </div>
      </Row>
      <InlineError error={props.errors['current-password']} />
      <ButtonContainer vertical>
        <Button
          disabled={!submitEnabled}
          large
          appearance="primary"
          caption={t('accounts:createAccount', 'Create account')}
          progressCaption={t(
            'accounts:creatingAccountProgress',
            'Creating account',
          )}
          progressIndicator={props.passwordChangeInProgress}
          onClick={props.onSubmit}
        />
      </ButtonContainer>
    </div>
  )
}

export default ChangePasswordWelcomeForm
